import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { conversationId } from '../../helpers/conversationId';
import { useConversations } from '../../hooks/useDirectMessages';
import { ConversationList } from './ConversationList';
import { ConversationThread } from './ConversationThread';
import styles from './directMessages.module.scss';
import { NewMessageComposer } from './NewMessageComposer';

/**
 * TRIL-394: Direct messages page. Master-detail: the inbox (thread links) plus the
 * open conversation. Selection lives in the `?c=<convId>` search param so a thread
 * is deep-linkable and survives reload. On mobile, one pane shows at a time.
 */
export default function DirectMessages() {
  const [params, setParams] = useSearchParams();
  const activeConvId = params.get('c') ?? undefined;
  const { conversations, loading } = useConversations();
  const active = conversations.find((c) => c.convId === activeConvId);
  // TRIL-402: a brand-new thread opened from an entry point isn't in the inbox yet,
  // so fall back to the ?u=<username> hint to show the counterparty in the header.
  const otherUsernameParam = params.get('u') ?? undefined;
  const otherUser =
    active?.otherUser ??
    (otherUsernameParam ? { id: '', username: otherUsernameParam } : undefined);

  const { user } = useAuth();
  // TRIL-402: start a DM with a searched user — open the (possibly new) thread.
  const startDm = (otherUserId: string, username: string) => {
    if (!user?.id) return;
    const next = new URLSearchParams(params);
    next.set('c', conversationId(user.id, otherUserId));
    next.set('u', username);
    setParams(next);
  };

  const select = (convId: string) => {
    const next = new URLSearchParams(params);
    next.set('c', convId);
    setParams(next);
  };

  const clear = () => {
    const next = new URLSearchParams(params);
    next.delete('c');
    setParams(next);
  };

  return (
    <div className={styles.dm}>
      <aside
        className={`${styles.listPane} ${activeConvId ? styles.hideOnMobile : ''}`}
      >
        <h1 className={styles.heading}>Messages</h1>
        <NewMessageComposer onPick={startDm} />
        <ConversationList
          conversations={conversations}
          loading={loading}
          activeConvId={activeConvId}
          onSelect={select}
        />
      </aside>

      <section
        className={`${styles.threadPane} ${activeConvId ? '' : styles.hideOnMobile}`}
      >
        {activeConvId ? (
          <ConversationThread
            key={activeConvId}
            convId={activeConvId}
            otherUser={otherUser}
            onBack={clear}
          />
        ) : (
          <div className={styles.empty}>
            <p>Select a conversation to start chatting.</p>
          </div>
        )}
      </section>
    </div>
  );
}
